import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle, X, Calendar, Clock, User, Crown } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import OrderChip from '../Brand/OrderChip';
import RatingBadge from '../Staff/RatingBadge';
import useStaffScores from '../../lib/useStaffScores';
import { SkeletonCards } from '../Brand/BrandLoading';
import { ukDate, ukTime, money, pct } from '../../lib/format';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const profitColour = (pct, settings) => {
  if (pct === null || pct === undefined) return 'text-slate-400';
  if (pct >= (settings?.green_min ?? 40)) return 'text-green-700';
  if (pct >= (settings?.amber_min ?? 20)) return 'text-amber-700';
  return 'text-red-600';
};

const Line = ({ label, value, strong, className = '' }) => (
  <div className="flex items-center justify-between gap-2 py-0.5">
    <span className="text-xs text-slate-500">{label}</span>
    <span className={`bd-money text-xs ${strong ? 'font-bold' : 'font-semibold'} ${className || 'text-ink'}`}>
      {value}
    </span>
  </div>
);

// Admin only: what each candidate costs and the profit if they take the job
export const JobRequestsPanel = ({ token, requests, onApprove, onDeny }) => {
  const [costing, setCosting] = useState({});
  const [loading, setLoading] = useState(true);
  const scores = useStaffScores(token);

  const groups = Object.values((requests || []).reduce((acc, request) => {
    const key = request.job_id;
    if (!acc[key]) acc[key] = { jobId: key, job: request, requests: [] };
    acc[key].requests.push(request);
    return acc;
  }, {}));

  useEffect(() => {
    const load = async () => {
      const headers = { Authorization: `Bearer ${token}` };
      const ids = groups.map((group) => group.jobId);
      if (ids.length === 0) { setLoading(false); return; }
      try {
        // Work out any missing distances for just these requesters, in one batch per job
        const emailsByJob = Object.fromEntries(groups.map((group) => [
          group.jobId,
          [...new Set(group.requests.map((request) => request.requested_by_email))].join(','),
        ]));
        const results = await Promise.all(ids.map((id) =>
          axios.get(`${API}/admin/jobs/${id}/costing`, {
            headers,
            params: { calculate: true, emails: emailsByJob[id] },
          })
            .then((res) => [id, res.data])
            .catch(() => [id, null])
        ));
        setCosting(Object.fromEntries(results));
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line
  }, [token, (requests || []).map((r) => r.id).join(',')]);

  if (loading) return <SkeletonCards count={2} testId="job-requests-loading" />;

  return (
    <div className="space-y-6" data-testid="job-requests-panel">
      {groups.map(({ jobId, job, requests: rows }) => {
        const data = costing[jobId];
        const settings = data?.profit_settings;
        const byEmail = Object.fromEntries((data?.staff || []).map((row) => [row.staff_email, row]));

        const candidates = [...rows].map((request) => ({
          request,
          figures: byEmail[request.requested_by_email] || null,
        })).sort((a, b) => {
          const left = a.figures?.projected_profit_gbp;
          const right = b.figures?.projected_profit_gbp;
          if (left === right) return 0;
          if (left === null || left === undefined) return 1;
          if (right === null || right === undefined) return -1;
          return right - left;
        });

        return (
          <Card key={jobId} className="bd-card-accent" data-testid={`request-group-${jobId}`}>
            <CardHeader className="bg-cream pb-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-base">{job.job_type}</CardTitle>
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-slate-600">
                    <OrderChip value={job.order_number || job.job_number} fallback="" />
                    <span className="flex items-center gap-1"><User className="h-4 w-4 text-gold-deep" />{job.client_name}</span>
                    <span className="flex items-center gap-1"><Calendar className="h-4 w-4 text-gold-deep" />{ukDate(job.date)}</span>
                    {job.time && <span className="flex items-center gap-1"><Clock className="h-4 w-4 text-gold-deep" />{ukTime(job.time) || job.time}</span>}
                  </div>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  {data?.job?.job_full && (
                    <Badge variant="secondary" className="border-gold text-gold-deep"
                           data-testid={`job-full-${jobId}`}>
                      Job already full
                    </Badge>
                  )}
                  <Badge variant="secondary">{rows.length} request{rows.length !== 1 ? 's' : ''}</Badge>
                </div>
              </div>

              {data && (
                <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-600"
                     data-testid={`request-job-money-${jobId}`}>
                  <span>Price <strong className="bd-money text-ink">{data.job.has_price ? money(data.job.job_value) : 'No price'}</strong></span>
                  <span>Wage each <strong className="bd-money text-ink">{money(data.job.wage_each)}</strong></span>
                  <span>Bonus each <strong className="bd-money text-ink">{money(data.job.bonus_each)}</strong></span>
                  <span>Ingredients &amp; supplies <strong className="bd-money text-ink">{money(data.job.ingredients_cost)}</strong></span>
                  <span>Needs <strong className="text-ink">{data.job.required_staff_count}</strong>, {data.job.assigned_staff_count} assigned</span>
                  {data.job.assigned_staff_count > 0 && (
                    <span>Already committed <strong className="bd-money text-ink">{money(data.job.assigned_cost)}</strong></span>
                  )}
                </div>
              )}
            </CardHeader>

            <CardContent className="pt-4">
              <p className="mb-3 text-sm font-semibold text-slate-700">
                {rows.length === 1 ? 'Staff requesting this job' : 'Compare all candidates, best margin first'}
              </p>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {candidates.map(({ request, figures }) => (
                  <div key={request.id}
                       className={`rounded-[12px] border p-4 transition-colors ${figures?.best_margin ? 'border-gold bg-cream/60' : 'border-hair hover:border-gold'}`}
                       data-testid={`request-card-${request.id}`}>
                    <div className="mb-3 flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h4 className="font-bold text-ink">{request.requested_by_name}</h4>
                        <p className="text-xs text-slate-500">{request.requested_by_email}</p>
                        <RatingBadge
                          className="mt-1"
                          score={scores[request.requested_by_email]?.score}
                          reliability={scores[request.requested_by_email]?.reliability}
                          testId={`request-rating-${request.id}`}
                        />
                      </div>
                      {figures?.best_margin && (
                        <Badge className="shrink-0" data-testid={`best-margin-${request.id}`}>
                          <Crown className="mr-1 h-3 w-3" /> Best margin
                        </Badge>
                      )}
                    </div>

                    {figures ? (
                      <div className="mb-3 rounded-[10px] border border-hair bg-white p-3">
                        <Line label="Wage" value={money(figures.wage)} />
                        <Line
                          label="Travel money"
                          value={figures.travel === null ? figures.travel_message : money(figures.travel)}
                          className={figures.travel === null ? 'text-amber-700' : 'text-ink'}
                        />
                        {figures.travel_miles !== null && figures.travel_miles !== undefined && (
                          <p className="pb-1 text-right text-[10px] text-slate-400">
                            {figures.travel_miles} miles each way
                          </p>
                        )}
                        <Line label="Bonus" value={money(figures.bonus)} />
                        <Line label="Ingredients &amp; supplies" value={money(figures.ingredients_cost)} />
                        <div className="mt-2 border-t border-hair pt-2">
                          <Line label="Total cost if they do it" value={money(figures.projected_total_costs)} strong />
                          <Line
                            label="Profit if assigned"
                            value={figures.projected_profit_gbp === null
                              ? 'No price on this job'
                              : `${money(figures.projected_profit_gbp)} (${pct(figures.projected_profit_percentage)})`}
                            strong
                            className={profitColour(figures.projected_profit_percentage, settings)}
                          />
                        </div>
                        {figures.already_assigned && (
                          <p className="mt-2 text-[11px] font-semibold text-gold-deep">
                            Already assigned to this job
                          </p>
                        )}
                        {figures.replaces_assigned && (
                          <p className="mt-2 text-[11px] font-semibold text-gold-deep"
                             data-testid={`replaces-assigned-${request.id}`}>
                            Profit shown as if they replaced the person currently assigned
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="mb-3 text-xs text-amber-700">
                        No costing for this person yet.
                      </p>
                    )}

                    {request.message && (
                      <div className="mb-3 rounded-[8px] bg-cream p-2">
                        <p className="text-xs text-slate-700">{request.message}</p>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Button onClick={() => onApprove(request.id)} size="sm" className="flex-1"
                              data-testid={`approve-request-${request.id}`}>
                        <CheckCircle className="mr-1 h-3 w-3" /> Approve
                      </Button>
                      <Button onClick={() => onDeny(request.id)} size="sm" variant="outline"
                              className="text-red-600"
                              data-testid={`deny-request-${request.id}`}>
                        <X className="mr-1 h-3 w-3" /> Deny
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {rows.length > 1 && (
                <p className="mt-3 rounded-[10px] bg-cream p-3 text-sm text-ink">
                  <strong>Multiple requests:</strong> approving one will automatically deny the rest.
                  Figures are admin only — staff never see them.
                </p>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default JobRequestsPanel;
